import { ApiFill } from '#/api/types';
import { EXCHANGE_DECIMALS } from '#/config/constants';
import { BigNumberJs } from '#/lib/BigNumber';

type AggregatedFill<T extends ApiFill = ApiFill> = T & {
  /** How many fills the aggregation group of this row contains (1 = not aggregated) */
  aggregatedFillsCount: number;
  /** Aggregation group key (see getFillAggregationKey), undefined for fills without orderId */
  aggregationKey?: string;
  /** True when the aggregation group is expanded and this row is one of its individual fills */
  aggregationExpanded?: boolean;
  /** 1-based position of the row within the expanded aggregation group, counted from the bottom (last row = 1) */
  aggregatedFillIndex?: number;
};

/**
 * Fills of the same order are merged only when they were executed at the same
 * time (single execution matched against multiple resting orders) and share
 * type, liquidity and settlement status - otherwise they stay as separate rows,
 * so partial fills executed at different times are displayed separately.
 * Fills without orderId (liquidation, deleverage, closure) are never merged.
 */
export const getFillAggregationKey = (fill: ApiFill) =>
  fill.orderId
    ? [
        fill.orderId,
        fill.time,
        fill.type,
        fill.liquidity ?? '',
        fill.txStatus,
      ].join('|')
    : undefined;

const sumOptional = (a: string | undefined, b: string | undefined) => {
  if (a === undefined && b === undefined) {
    return undefined;
  }
  return BigNumberJs(a ?? 0)
    .plus(b ?? 0)
    .toFixed();
};

const mergeFills = <T extends ApiFill>(
  aggregated: AggregatedFill<T>,
  fill: T
): AggregatedFill<T> => {
  const quantity = BigNumberJs(aggregated.quantity).plus(fill.quantity);
  const quoteQuantity = BigNumberJs(aggregated.quoteQuantity).plus(
    fill.quoteQuantity
  );
  return {
    ...aggregated,
    aggregatedFillsCount: aggregated.aggregatedFillsCount + 1,
    quantity: quantity.toFixed(),
    quoteQuantity: quoteQuantity.toFixed(),
    // Volume weighted average price
    price: quantity.isZero()
      ? aggregated.price
      : quoteQuantity.dividedBy(quantity).toFixed(EXCHANGE_DECIMALS),
    realizedPnL: BigNumberJs(aggregated.realizedPnL)
      .plus(fill.realizedPnL)
      .toFixed(),
    fee: sumOptional(aggregated.fee, fill.fee),
    builderFee: sumOptional(aggregated.builderFee, fill.builderFee),
    // Different settlement transactions can't be represented by a single link
    txId: aggregated.txId === fill.txId ? aggregated.txId : null,
  };
};

/**
 * Merge fills belonging to the same order execution into a single row.
 * Fills in the API list are not guaranteed to be adjacent (fills of other
 * orders can be in between) - each row is placed at the position of the first
 * fill of its group.
 *
 * @param expandedAggregationKey group displayed as individual fills
 *  (placed next to each other) instead of a single merged row
 */
export function aggregateFillsByOrder<T extends ApiFill>(
  fills: T[],
  expandedAggregationKey?: string
): AggregatedFill<T>[] {
  // Each slot holds rows of one aggregation group (or a single fill without orderId)
  const slots: AggregatedFill<T>[][] = [];
  const slotByKey = new Map<string, AggregatedFill<T>[]>();

  for (const fill of fills) {
    const key = getFillAggregationKey(fill);
    if (key === undefined) {
      slots.push([{ ...fill, aggregatedFillsCount: 1 }]);
      continue;
    }

    let slot = slotByKey.get(key);
    if (!slot) {
      slot = [];
      slotByKey.set(key, slot);
      slots.push(slot);
    }

    if (key === expandedAggregationKey) {
      slot.push({
        ...fill,
        aggregatedFillsCount: 1,
        aggregationKey: key,
        aggregationExpanded: true,
      });
    } else if (slot.length === 0) {
      slot.push({ ...fill, aggregatedFillsCount: 1, aggregationKey: key });
    } else {
      slot[0] = mergeFills(slot[0], fill);
    }
  }

  // Individual rows of the expanded group carry the size of the whole group
  // and their position within it (numbered from the bottom, as the list is
  // ordered newest first)
  const expandedSlot = expandedAggregationKey
    ? slotByKey.get(expandedAggregationKey)
    : undefined;
  if (expandedSlot) {
    expandedSlot.forEach((row, index) => {
      row.aggregatedFillsCount = expandedSlot.length;
      row.aggregatedFillIndex = expandedSlot.length - index;
    });
  }

  return slots.flat();
}
